import { ChevronDown, Heart, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useStore } from "../../context/StoreContext";
import { NAV_ITEMS, SHOP_DROPDOWN_ITEMS } from "../../lib/siteContent";
import { useTheme } from "../../context/ThemeContext";
import { ThemeToggle } from "./ThemeToggle";

export const Header = () => {
  const location = useLocation();
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const closeTimerRef = useRef(null);
  const { cartCount, wishlist } = useStore();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const openShopDropdown = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    setShopOpen(true);
  };

  const closeShopDropdownWithDelay = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setShopOpen(false);
    }, 180);
  };

  const navItems = useMemo(() => NAV_ITEMS, []);
  const shopItems = useMemo(() => SHOP_DROPDOWN_ITEMS, []);
  const navItemsWithoutShop = useMemo(() => navItems.filter((item) => item.label !== "Shop the Drop"), [navItems]);
  const isHome = location.pathname === "/";
  const isShopActive = location.pathname === "/shop" || shopItems.some((item) => item.to === location.pathname);
  const darkBg = isHome && !scrolled ? "bg-[#05070D]/60" : "bg-[#05070D]";
  const lightBg = isHome && !scrolled ? "bg-[#faf7f1]/85" : "bg-[#faf7f1]";
  const headerBackground = isDark ? darkBg : lightBg;
  const logoSrc = isDark ? "/assets/logo-dark-bg.png" : "/assets/logo-light-bg.png";
  const iconTone = isDark ? "text-[#efe8dc]" : "text-[#2e2a24]";
  const navTone = isDark ? "text-[#e6e0d4] hover:text-white" : "text-[#352f27] hover:text-black";
  const borderTone = isDark ? "border-white/15" : "border-black/20";
  const counterBg = isDark ? "bg-[#bfa46f] text-black" : "bg-[#c2a45d] text-black";
  const mobileOverlayBg = isDark ? "bg-[#05070D]" : "bg-[#f8f4ec]";
  const mobileOverlayBorder = isDark ? "border-white/10" : "border-black/15";
  const mobileTitleTone = isDark ? "text-[#d9ccb2]" : "text-[#3a3329]";
  const mobileLinkTone = isDark ? "text-[#f1ebdf]" : "text-[#201b15]";
  const mobileSubItemTone = isDark ? "text-[#d7ccb8]" : "text-[#3a3327]";
  const mobileIconTone = isDark ? "text-white border-white/15" : "text-[#2c251e] border-black/20";

  return (
    <header
      className={`sticky top-0 z-50 border-b ${isDark ? "border-white/10" : "border-black/10"} backdrop-blur-md transition-colors duration-300 ${headerBackground}`}
      data-testid="global-header"
    >
      <div
        className={`px-3 py-2 text-center text-[11px] leading-tight sm:px-4 sm:text-xs ${isDark ? "bg-[#0d0f15] text-[#d8ccb1]" : "bg-[#efe6d6] text-[#3d362d]"}`}
        data-testid="header-announcement-banner"
      >
        Free shipping across India on orders above ₹999
      </div>
      <div className="mx-auto flex h-20 w-full max-w-[1400px] items-center justify-between px-3 sm:h-[5.5rem] sm:px-6 lg:px-8 xl:h-24">
        <button
          className={`inline-flex rounded-full border p-2 transition hover:border-[#bfa46f] xl:hidden ${borderTone} ${iconTone}`}
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-open-button"
        >
          <Menu size={18} />
        </button>

        <Link to="/" className="inline-flex items-center" data-testid="header-logo-link">
          <img
            src={logoSrc}
            alt="Eternal Order"
            className="h-12 w-auto object-contain sm:h-14 xl:h-16"
            data-testid="header-logo-image"
          />
        </Link>

        <nav className="hidden items-center gap-3 xl:flex xl:gap-4 2xl:gap-5" data-testid="desktop-nav">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `text-[11px] font-semibold uppercase tracking-[0.12em] leading-none transition ${isActive ? "text-[#C2A45D]" : navTone}`
            }
            data-testid="nav-link-home"
          >
            Home
          </NavLink>

          <div
            className="relative"
            onMouseEnter={openShopDropdown}
            onMouseLeave={closeShopDropdownWithDelay}
            data-testid="desktop-shop-dropdown-wrapper"
          >
            <div className="flex items-center gap-1 leading-none" data-testid="desktop-shop-dropdown-trigger-row">
              <NavLink
                to="/shop"
                className={`text-[11px] font-semibold uppercase tracking-[0.12em] leading-none transition ${isShopActive ? "text-[#C2A45D]" : navTone}`}
                data-testid="nav-link-shop-the-drop"
              >
                Shop the Drop
              </NavLink>
              <button
                type="button"
                onClick={() => setShopOpen((prev) => !prev)}
                className={`inline-flex items-center rounded-full p-1 leading-none transition ${isShopActive ? "text-[#C2A45D]" : navTone}`}
                data-testid="desktop-shop-dropdown-toggle-button"
              >
                <ChevronDown size={12} className={`transition-transform ${shopOpen ? "rotate-180" : "rotate-0"}`} />
              </button>
            </div>
            <div className="absolute left-0 right-0 top-full h-3" aria-hidden="true" />

            {shopOpen && (
              <div
                className={`absolute left-0 top-8 min-w-[180px] rounded-xl border ${isDark ? "border-white/10 bg-[#07090f]" : "border-black/15 bg-[#fbf7ef]"} p-2 shadow-xl`}
                onMouseEnter={openShopDropdown}
                onMouseLeave={closeShopDropdownWithDelay}
                data-testid="desktop-shop-dropdown-menu"
              >
                {shopItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setShopOpen(false)}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-2 text-xs uppercase tracking-[0.12em] transition ${
                        isActive ? "text-[#C2A45D]" : isDark ? "text-[#ddd4c4] hover:bg-white/5" : "text-[#2f2a24] hover:bg-black/5"
                      }`
                    }
                    data-testid={`desktop-shop-dropdown-item-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {navItemsWithoutShop
            .filter((item) => item.label !== "Home")
            .map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `text-[11px] font-semibold uppercase tracking-[0.12em] leading-none transition ${isActive ? "text-[#C2A45D]" : navTone}`
                }
                data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {item.label}
              </NavLink>
            ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2.5">
          <Link
            to="/search"
            className={`hidden rounded-full border p-2 transition hover:border-[#C2A45D] md:inline-flex ${borderTone} ${iconTone}`}
            data-testid="header-search-link"
          >
            <Search size={16} />
          </Link>
          <ThemeToggle />
          <Link
            to="/wishlist"
            className={`relative hidden rounded-full border p-2 transition hover:border-[#bfa46f] sm:inline-flex ${borderTone} ${iconTone}`}
            data-testid="header-wishlist-link"
          >
            <Heart size={16} />
            <span className={`absolute -right-1 -top-1 rounded-full px-1.5 text-[10px] ${counterBg}`} data-testid="header-wishlist-count">
              {wishlist.length}
            </span>
          </Link>
          <Link
            to="/account"
            className={`hidden rounded-full border p-2 transition hover:border-[#bfa46f] sm:inline-flex ${borderTone} ${iconTone}`}
            data-testid="header-account-link"
          >
            <User size={16} />
          </Link>
          <Link
            to="/cart"
            className={`relative rounded-full border p-2 transition hover:border-[#bfa46f] ${borderTone} ${iconTone}`}
            data-testid="header-cart-link"
          >
            <ShoppingBag size={16} />
            <span className={`absolute -right-1 -top-1 rounded-full px-1.5 text-[10px] ${counterBg}`} data-testid="header-cart-count">
              {cartCount}
            </span>
          </Link>
        </div>
      </div>

      {open && (
        <div
          className={`fixed left-0 top-0 z-[120] h-screen min-h-screen w-screen overflow-y-auto p-0 xl:hidden ${mobileOverlayBg}`}
          data-testid="mobile-menu-overlay"
        >
          <div className={`min-h-screen w-full rounded-none p-6 ${mobileOverlayBg}`}>
            <div className="mb-8 flex items-center justify-between">
              <p className={`text-xs tracking-[0.25em] ${mobileTitleTone}`} data-testid="mobile-menu-title">
                NAVIGATION
              </p>
              <button
                className={`rounded-full border p-2 ${mobileIconTone}`}
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close-button"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-4">
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className={`block border-b pb-3 text-lg ${mobileOverlayBorder} ${mobileLinkTone}`}
                data-testid="mobile-nav-link-home"
              >
                Home
              </Link>

              <div className={`border-b pb-3 ${mobileOverlayBorder}`} data-testid="mobile-shop-accordion-wrapper">
                <div className="flex items-center justify-between">
                  <Link
                    to="/shop"
                    onClick={() => setOpen(false)}
                    className={`text-lg ${mobileLinkTone}`}
                    data-testid="mobile-nav-link-shop-the-drop"
                  >
                    Shop the Drop
                  </Link>
                  <button
                    type="button"
                    onClick={() => setMobileShopOpen((prev) => !prev)}
                    className={`rounded-full border p-1.5 ${mobileIconTone}`}
                    data-testid="mobile-shop-accordion-toggle-button"
                  >
                    <ChevronDown size={14} className={`transition-transform ${mobileShopOpen ? "rotate-180" : "rotate-0"}`} />
                  </button>
                </div>

                {mobileShopOpen && (
                  <div className="mt-3 space-y-2 pl-3" data-testid="mobile-shop-accordion-menu">
                    {shopItems.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className={`block text-sm uppercase tracking-[0.12em] ${mobileSubItemTone}`}
                        data-testid={`mobile-shop-accordion-item-${item.label.toLowerCase()}`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navItemsWithoutShop
                .filter((item) => item.label !== "Home")
                .map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className={`block border-b pb-3 text-lg ${mobileOverlayBorder} ${mobileLinkTone}`}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {item.label}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
